import React from 'react';
import { Calendar, Users, MapPin, Euro, TrendingUp, CheckCircle, MessageSquare, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// City images for visual variety
const CITY_IMAGES = {
  'Roma': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80',
  'Milano': 'https://images.unsplash.com/photo-1513581166391-887a96ddeafd?w=800&q=80',
  'Firenze': 'https://images.unsplash.com/photo-1543429258-c5ca3a1d5f41?w=800&q=80',
  'Venezia': 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=800&q=80',
  'Napoli': 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=800&q=80',
  'Amalfi': 'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?w=800&q=80',
  'Positano': 'https://images.unsplash.com/photo-1534113414509-0eec2bfb493f?w=800&q=80',
  'Taormina': 'https://images.unsplash.com/photo-1559896324-2322b83f34a5?w=800&q=80',
  'Sorrento': 'https://images.unsplash.com/photo-1598135753163-6167c1a1ad65?w=800&q=80',
  'Como': 'https://images.unsplash.com/photo-1582547575721-4b7b5c0a4f9a?w=800&q=80',
  'Verona': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'Bologna': 'https://images.unsplash.com/photo-1582639590011-f5a8416d1101?w=800&q=80',
  'Torino': 'https://images.unsplash.com/photo-1568291929-e30df4f37c82?w=800&q=80',
  'Palermo': 'https://images.unsplash.com/photo-1523365280197-f1783db9fe62?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1499678329028-101435549a4e?w=800&q=80'
};

const getCityImage = (city) => {
  if (!city) return CITY_IMAGES.default;
  const normalizedCity = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
  return CITY_IMAGES[normalizedCity] || CITY_IMAGES.default;
};

const RequestCardModern = ({ 
  request, 
  onMakeOffer, 
  isOwnerView = false,
  hasMyOffer = false,
  interestCount = 0,
  offersReceived = []
}) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  const cityImage = getCityImage(request.city);

  return (
    <Card className="group overflow-hidden bg-white hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border-0 shadow-lg">
      {/* Image Section */}
      <div className="relative h-40 overflow-hidden">
        <img 
          src={cityImage}
          alt={request.city || 'Destination'}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          onError={(e) => {
            e.target.src = CITY_IMAGES.default;
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {request.status === 'APERTO' && (
            <Badge className="bg-emerald-500 text-white border-0 font-semibold">
              Attiva
            </Badge>
          )}
          {request.trip_type && (
            <Badge className="bg-white/90 backdrop-blur-sm text-slate-700 border-0 capitalize">
              {request.trip_type}
            </Badge>
          )}
        </div>

        {/* Interest Indicator for Owner View */}
        {isOwnerView && interestCount > 0 && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-amber-500 text-white border-0 font-semibold flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {interestCount} {interestCount === 1 ? 'interessato' : 'interessati'}
            </Badge>
          </div>
        )}

        {/* Bottom Info on Image */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 text-white mb-1">
            <MapPin className="w-5 h-5" />
            <h3 className="text-xl font-bold drop-shadow-lg">{request.city}</h3>
          </div>
          {isOwnerView && (
            <p className="text-white/70 text-sm">Viaggiatore anonimo</p>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5">
        {/* Date and Guests Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Date</p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(request.check_in_date)} - {formatDate(request.check_out_date)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Ospiti</p>
              <p className="text-sm font-semibold text-slate-900">
                {request.guests || request.num_guests || 2} persone
              </p>
            </div>
          </div>
        </div>

        {/* Budget */}
        <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Euro className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-emerald-700 font-medium">Budget per notte</p>
            <p className="text-lg font-bold text-emerald-900">
              €{request.budget_min} - €{request.budget_max}
            </p>
          </div>
        </div>

        {/* Notes */}
        {request.notes && (
          <p className="text-sm text-slate-600 mb-4 line-clamp-2 italic">
            "{request.notes}"
          </p>
        )}

        {/* Offers Received (Traveler View) */}
        {!isOwnerView && offersReceived && offersReceived.length > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">
                {offersReceived.length} {offersReceived.length === 1 ? 'Offerta Ricevuta' : 'Offerte Ricevute'}
              </span>
            </div>
            <div className="space-y-2">
              {offersReceived.slice(0, 2).map((offer) => (
                <div key={offer.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">{offer.property_name}</p>
                    <p className="text-xs text-slate-500">{offer.property_owner?.full_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">€{offer.offered_price}</p>
                    <Badge variant="outline" className="text-xs">
                      {offer.status === 'pending' ? 'In attesa' : 
                       offer.status === 'accepted' ? 'Accettata' :
                       offer.status === 'countered' ? 'Contro-offerta' : offer.status}
                    </Badge>
                  </div>
                </div>
              ))}
              {offersReceived.length > 2 && (
                <p className="text-xs text-blue-600 text-center">
                  +{offersReceived.length - 2} altre offerte
                </p>
              )}
            </div>
          </div>
        )}

        {/* Owner View: My Offer Indicator */}
        {isOwnerView && hasMyOffer && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-800">Hai già fatto un'offerta</span>
          </div>
        )}

        {/* Action Button */}
        {onMakeOffer && (
          <Button 
            onClick={() => onMakeOffer(request)}
            className={`w-full ${hasMyOffer 
              ? 'bg-slate-200 text-slate-500 cursor-not-allowed' 
              : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
            disabled={hasMyOffer}
          >
            {hasMyOffer ? 'Offerta Inviata' : "Fai un'Offerta"}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default RequestCardModern;
